import { forwardRef } from 'react'

import { Button } from '../../components/Button'
import { Card } from '../../components/Card'
import { Input } from '../../components/Input'
import { Skeleton } from '../../components/Skeleton'
import { Spinner } from '../../components/Spinner'
import { PaymentMethodSelector } from '../PaymentMethodSelector'
import styles from './CheckoutForm.module.css'

import type { PaymentMethodIntentReason, PaymentMethodOption } from '../PaymentMethodSelector'
import type { CheckoutPayerInput, PaymentMethodType } from '@checkout/core'

export interface CheckoutFormData {
  email: string
  paymentMethod: PaymentMethodType
  payer: CheckoutPayerInput
}

export type EmbeddedPaymentSectionVariant = 'panel' | 'button'

export interface CheckoutFormProps {
  /** 使用同一主按钮继续客户流程，不提交付款表单。 */
  primaryAction?: { label: string; onClick: () => void; disabled?: boolean }

  /** Whether payment methods are loading */
  isLoading?: boolean
  /** Error message for payment methods */
  error?: string | null
  /** Section 1 title */
  accountInfoTitle?: string
  /** Section 2 title */
  paymentInfoTitle?: string
  /** Whether to show section numbers */
  showSectionNumbers?: boolean
  /** Email value */
  email?: string
  /** Email change handler */
  onEmailChange?: (email: string) => void
  /** Email blur handler */
  onEmailBlur?: () => void
  /** Email validation error message */
  emailError?: string | null
  /** Email label */
  emailLabel?: string
  /** Email placeholder */
  emailPlaceholder?: string
  /** Whether email is read-only */
  emailReadOnly?: boolean
  /** Available payment methods */
  paymentMethods: PaymentMethodOption[]
  /** Currently selected payment method */
  selectedPaymentMethod: PaymentMethodType | null
  /** Payment method selection handler */
  onPaymentMethodSelect: (method: PaymentMethodType) => void
  /** Payment method preload intent handler */
  onPaymentMethodIntent?: (method: PaymentMethodOption, reason: PaymentMethodIntentReason) => void
  /** Content to render for the selected embedded payment method (inline under payment selector) */
  embeddedPaymentSection?: React.ReactNode
  /** Inline embedded payment section layout */
  embeddedPaymentSectionVariant?: EmbeddedPaymentSectionVariant
  /** Embedded payment section that must stay mounted across method switches */
  persistentEmbeddedPaymentSection?: React.ReactNode
  /** Stable embedded payment section layout */
  persistentEmbeddedPaymentSectionVariant?: EmbeddedPaymentSectionVariant
  /** Whether the stable embedded payment section is currently visible */
  showPersistentEmbeddedPaymentSection?: boolean
  /** Embedded payment details section that must stay mounted across method switches */
  persistentEmbeddedPaymentDetailsSection?: React.ReactNode
  /** Whether the stable embedded payment details section is currently visible */
  showPersistentEmbeddedPaymentDetailsSection?: boolean
  /** Billing information section for embedded card payment methods */
  billingInformationSection?: React.ReactNode
  /** Payer data collected by the billing information section */
  payer?: CheckoutPayerInput
  /** Whether the billing information section is valid */
  isBillingInformationValid?: boolean
  /** Embedded payment details section (shown as separate section 3 when required) */
  embeddedPaymentDetailsSection?: React.ReactNode
  /** Whether to show the embedded payment details section */
  showEmbeddedPaymentDetailsSection?: boolean
  /** Embedded payment details section title */
  embeddedPaymentDetailsTitle?: string
  /** Submit button label */
  submitLabel?: string
  /** Submit handler - receives form data */
  onSubmit?: (data: CheckoutFormData) => void
  /** Whether form is submitting */
  isSubmitting?: boolean
  /** Whether to hide submit button (e.g., when payment form has its own button) */
  hideSubmitButton?: boolean
  /** Whether the embedded payment section is valid */
  isEmbeddedPaymentValid?: boolean
  /** Alert message to display above the submit button (e.g., cooldown warning) */
  alertMessage?: string | null
  /** Terms text */
  termsText?: React.ReactNode
  /** Additional class name */
  className?: string
}

/**
 * CheckoutForm - Combined checkout form with account info and payment selection
 */
export const CheckoutForm = forwardRef<HTMLDivElement, CheckoutFormProps>(
  (
    {
      primaryAction,
      isLoading = false,
      error,
      accountInfoTitle = 'Account Info',
      paymentInfoTitle = 'Payment Info',
      showSectionNumbers = true,
      email = '',
      onEmailChange,
      onEmailBlur,
      emailError,
      emailLabel = 'Email',
      emailPlaceholder = 'your@email.com',
      emailReadOnly = false,
      paymentMethods,
      selectedPaymentMethod,
      onPaymentMethodSelect,
      onPaymentMethodIntent,
      embeddedPaymentSection,
      embeddedPaymentSectionVariant = 'panel',
      persistentEmbeddedPaymentSection,
      persistentEmbeddedPaymentSectionVariant = 'panel',
      showPersistentEmbeddedPaymentSection = false,
      persistentEmbeddedPaymentDetailsSection,
      showPersistentEmbeddedPaymentDetailsSection = false,
      billingInformationSection,
      payer,
      isBillingInformationValid = true,
      embeddedPaymentDetailsSection,
      showEmbeddedPaymentDetailsSection,
      embeddedPaymentDetailsTitle = 'Payment Details',
      submitLabel = 'Subscribe',
      onSubmit,
      isSubmitting = false,
      hideSubmitButton = false,
      isEmbeddedPaymentValid = true,
      alertMessage,
      termsText,
      className,
    },
    ref
  ) => {
    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onEmailChange?.(e.target.value)
    }

    const handleEmailBlur = () => {
      onEmailBlur?.()
    }

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault()
      // 隐藏外部提交时，交由对应的支付入口处理
      if (primaryAction) {
        if (!isSubmitting && !primaryAction.disabled) primaryAction.onClick()
        return
      }
      if (hideSubmitButton) {
        return
      }
      if (selectedPaymentMethod && email) {
        onSubmit?.({
          email,
          paymentMethod: selectedPaymentMethod,
          payer: payer ?? { email },
        })
      }
    }

    const hasPaymentMethods = paymentMethods.length > 0
    const isRefreshingPaymentMethods = isLoading && hasPaymentMethods
    const showInitialLoadingState = isLoading && !hasPaymentMethods
    const showBlockingErrorState = Boolean(error && !isLoading && !hasPaymentMethods)
    const showPaymentMethodSelector = !showInitialLoadingState && !showBlockingErrorState

    const isSubmitDisabled = primaryAction
      ? isSubmitting || primaryAction.disabled
      : isSubmitting ||
        !selectedPaymentMethod ||
        !email ||
        isLoading ||
        showBlockingErrorState ||
        !isEmbeddedPaymentValid ||
        !isBillingInformationValid
    const hasPaymentDetailsSection = Boolean(
      billingInformationSection ||
      embeddedPaymentDetailsSection ||
      persistentEmbeddedPaymentDetailsSection
    )
    const shouldShowPaymentDetailsSection =
      showEmbeddedPaymentDetailsSection === false
        ? showPersistentEmbeddedPaymentDetailsSection
        : Boolean(
            billingInformationSection ||
            embeddedPaymentDetailsSection ||
            showPersistentEmbeddedPaymentDetailsSection
          )

    return (
      <Card ref={ref} className={`${styles.container} ${className ?? ''}`}>
        <form onSubmit={handleSubmit}>
          {/* Section 1: Account Info */}
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>
              {showSectionNumbers && <span className={styles.sectionNumber}>1.</span>}
              {accountInfoTitle}
            </h3>
            <div className={styles.sectionContent}>
              <Input
                type="email"
                label={emailLabel}
                value={email}
                onChange={handleEmailChange}
                onBlur={handleEmailBlur}
                placeholder={emailPlaceholder}
                readOnly={emailReadOnly}
                disabled={isSubmitting}
                autoComplete="email"
                error={emailError ?? undefined}
                data-ga-module="customer_info"
              />
            </div>
          </section>

          {/* Section 2: Payment Info */}
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>
              {showSectionNumbers && <span className={styles.sectionNumber}>2.</span>}
              {paymentInfoTitle}
            </h3>
            <div className={styles.sectionContent}>
              {/* Loading state */}
              {showInitialLoadingState && (
                <div className={styles.loadingState}>
                  <Skeleton height={48} variant="rounded" />
                  <Skeleton height={48} variant="rounded" />
                </div>
              )}

              {showBlockingErrorState && (
                <div className={styles.errorState}>
                  <p>{error}</p>
                </div>
              )}

              {showPaymentMethodSelector && (
                <>
                  <div
                    className={`${styles.paymentMethodsShell} ${
                      isRefreshingPaymentMethods ? styles.paymentMethodsRefreshing : ''
                    }`}
                    aria-busy={isRefreshingPaymentMethods}
                  >
                    <PaymentMethodSelector
                      methods={paymentMethods}
                      selectedMethod={selectedPaymentMethod}
                      onSelect={onPaymentMethodSelect}
                      onIntent={onPaymentMethodIntent}
                      disabled={isSubmitting || isRefreshingPaymentMethods}
                      layout="horizontal"
                      showLabels={false}
                    />
                    {isRefreshingPaymentMethods && (
                      <div
                        className={styles.refreshIndicator}
                        role="status"
                        aria-label="Refreshing payment methods"
                      >
                        <Spinner size="sm" color="secondary" aria-hidden="true" />
                      </div>
                    )}
                  </div>

                  {!primaryAction && persistentEmbeddedPaymentSection && (
                    <div
                      className={`${persistentEmbeddedPaymentSectionVariant === 'button' ? styles.paymentButtonWrapper : styles.paymentFormWrapper} ${
                        showPersistentEmbeddedPaymentSection ? '' : styles.paymentPersistentHidden
                      }`}
                      aria-hidden={!showPersistentEmbeddedPaymentSection}
                    >
                      {persistentEmbeddedPaymentSection}
                    </div>
                  )}

                  {!primaryAction && embeddedPaymentSection && (
                    <div
                      className={
                        embeddedPaymentSectionVariant === 'button'
                          ? styles.paymentButtonWrapper
                          : styles.paymentFormWrapper
                      }
                    >
                      {embeddedPaymentSection}
                    </div>
                  )}
                </>
              )}
            </div>
          </section>

          {/* Section 3: Embedded Payment Details (when applicable) */}
          {!primaryAction && hasPaymentDetailsSection && (
            <section
              className={`${styles.section} ${
                shouldShowPaymentDetailsSection ? '' : styles.paymentDetailsSectionHidden
              }`}
            >
              <div className={styles.paymentDetailsPanel}>
                {billingInformationSection}
                {persistentEmbeddedPaymentDetailsSection && (
                  <div
                    className={`${styles.paymentDetailsGroup} ${
                      showPersistentEmbeddedPaymentDetailsSection
                        ? ''
                        : styles.paymentPersistentHidden
                    }`}
                    aria-hidden={!showPersistentEmbeddedPaymentDetailsSection}
                  >
                    {embeddedPaymentDetailsTitle && (
                      <h4 className={styles.paymentDetailsTitle}>{embeddedPaymentDetailsTitle}</h4>
                    )}
                    {persistentEmbeddedPaymentDetailsSection}
                  </div>
                )}
                {embeddedPaymentDetailsSection && (
                  <div className={styles.paymentDetailsGroup}>
                    {embeddedPaymentDetailsTitle && (
                      <h4 className={styles.paymentDetailsTitle}>{embeddedPaymentDetailsTitle}</h4>
                    )}
                    {embeddedPaymentDetailsSection}
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Alert message above button */}
          {alertMessage && <div className={styles.alertMessage}>{alertMessage}</div>}

          {primaryAction && error && (
            <div className={styles.errorState} role="alert">
              <p>{error}</p>
            </div>
          )}

          {/* Submit button area */}
          <div className={styles.submitWrapper}>
            {(primaryAction || !hideSubmitButton) && (
              <Button
                type="submit"
                variant="primary"
                size="xl"
                fixedWidth={286}
                isLoading={isSubmitting}
                disabled={isSubmitDisabled}
                data-ga-action-name="pay_button_click"
                data-ga-module="checkout_form"
              >
                {primaryAction?.label ?? submitLabel}
              </Button>
            )}
          </div>

          {/* Terms text */}
          {termsText && <p className={styles.terms}>{termsText}</p>}
        </form>
      </Card>
    )
  }
)

CheckoutForm.displayName = 'CheckoutForm'
